/**
 * PixiJS terrain overlay (read-only)
 * Bridge: window.__pixiRenderTerrain(zoneId, terrain)
 *
 * Goals (v2.5):
 * - Show readable “text cards” on terrain (no illustrations)
 * - Couché/Debout visible (tap/rotate)
 * - Desktop hover: pop-out (enlarge) + subtle premium effects
 * - Mobile: tap to pop-out (second tap closes)
 *
 * Debug: add ?debug or ?debug=1
 */
(function(){
  // PixiBoard v3.2 (small-slot layout + card names)
  try{ console.log('[Pixi] pixiBoard loaded v3.6'); }catch(e){}

  if (typeof window === 'undefined') return;

  const QS = new URLSearchParams(location.search);
  const DEBUG = QS.has('debug');

  // --- Placeholder illustration (used until real card thumbnails exist) ---
  // Put your PNG here: public/assets/creatures/placeholder.png
  // It will be served at: /assets/creatures/placeholder.png
  // Fallback sûr (existe dans le projet)
  const PLACEHOLDER_IMG_URL = '/assets/cards/placeholder.jpg';

  function resolveArtUrlCandidates(raw){
    let u = String(raw||'').trim();
    if(!u) return [PLACEHOLDER_IMG_URL];
    if (/^(https?:|data:|blob:)/i.test(u)) return [u];
    // Normalize paths. In production, assets are served from the site root.
    // Some exports store paths like "assets/..." or even "public/assets/...".
    // We generate a few candidates and always try the root-relative ones first
    // so PIXI textures resolve correctly (unlike the HTML <img> fallback chain).
    u = u.replace(/^\/+/, '');

    // If we are *not* inside /public/ (root index.html), assets are under public/assets
    const inPublic = (location && location.pathname && location.pathname.includes('/public/'));
    const cand = [];

    // 1) Root-relative (best for Cloudflare Pages)
    cand.push('/'+u);
    if (u.startsWith('assets/')) cand.push('/'+u);
    if (u.startsWith('public/')) cand.push('/'+u.replace(/^public\//, ''));

    // 2) Relative fallbacks (useful when opening /public/index.html directly)
    if (inPublic){
      cand.push(u);
      if (u.startsWith('assets/')) cand.push('public/'+u);
    } else {
      if (u.startsWith('assets/')) cand.push('public/'+u);
      cand.push(u);
    }
    // Always fallback to placeholder
    cand.push(PLACEHOLDER_IMG_URL);
    // De-dupe
    return [...new Set(cand)];
  }
  // Future full-card placeholder (used when you start exporting full card images)
  // Put your PNG here: public/assets/cards/placeholder.png
  const PLACEHOLDER_CARD_URL = '/assets/cards/placeholder.png';
  let __placeholderTex = null;
  const __artTexCache = new Map();

  // Level 1: preload all card artworks so miniatures don't show as black squares
  // before the first hover.
  let __preloadPromise = null;
  // Preload all card artworks (and placeholder) so board miniatures can render
  // immediately (no white/black rectangles while textures stream in).
  function preloadAllCardArt(cardsById){
    if(__preloadPromise) return __preloadPromise;
    __preloadPromise = (async () => {
      try{
        const unique = new Set();
        // Always include placeholder(s)
        unique.add(PLACEHOLDER_IMG_URL);
        unique.add(PLACEHOLDER_CARD_URL);

        for (const card of cardsById.values()){
          const raw = card?.artUrl || card?.image || card?.img || card?.illustration || '';
          const candidates = resolveArtUrlCandidates(raw);
          for (const u of candidates){
            if(!u) continue;
            unique.add(u);
          }
        }

        const urls = Array.from(unique);
        // Pixi v7/v8: Assets.load returns a Texture and caches it.
        if(PIXI.Assets && PIXI.Assets.load){
          await Promise.all(urls.map(async (u) => {
            try{
              const tex = await PIXI.Assets.load(u);
              if(tex) __artTexCache.set(u, tex);
              // Keep a handle to the placeholder texture
              if(u === PLACEHOLDER_IMG_URL && tex) __placeholderTex = tex;
            }catch(e){
              // Ignore individual failures; we'll fall back to placeholder.
            }
          }));
        }else{
          // Fallback: trigger lazy loads
          for(const u of urls) getArtTexture(u);
        }

        console.log(`[Pixi] Preloaded art textures: ${urls.length}`);
      }catch(e){
        console.warn('[Pixi] preloadAllCardArt failed', e);
      }
    })();
    return __preloadPromise;
  }
function getArtTexture(url){
  const key = (url && typeof url === "string") ? url : "/assets/cards/placeholder.png";
  if(__artTexCache.has(key)) return __artTexCache.get(key);
  try{
    const tex = PIXI.Texture.from(key);
    __artTexCache.set(key, tex);
    return tex;
  }catch(e){
    const tex = getPlaceholderTexture();
    __artTexCache.set(key, tex);
    return tex;
  }
}

function getPlaceholderTexture(){
    try{
      if (__placeholderTex) return __placeholderTex;
      // Pixi v8: Texture.from lazily loads and then caches the base texture
      __placeholderTex = (PIXI.Texture && PIXI.Texture.from) ? PIXI.Texture.from(PLACEHOLDER_IMG_URL) : null;
      // Hard fallback: never return null (null renders as a black square)
      if(!__placeholderTex) __placeholderTex = (PIXI.Texture && PIXI.Texture.WHITE) ? PIXI.Texture.WHITE : null;
      return __placeholderTex;
    }catch(e){
      return (PIXI.Texture && PIXI.Texture.WHITE) ? PIXI.Texture.WHITE : null;
    }
  }


  // --- Global hover popout (DOM, above all layers) ---
  let __hoverEl = null;
  function getHoverEl(){
    if (__hoverEl) return __hoverEl;
    const el = document.createElement('div');
    el.id = 'pixiHoverCard';
    el.style.position = 'fixed';
    el.style.left = '0px';
    el.style.top = '0px';
    el.style.zIndex = '999999';
    el.style.pointerEvents = 'none';
    el.style.display = 'none';
    el.style.maxWidth = '420px';
    el.style.padding = '12px 14px';
    el.style.borderRadius = '18px';
    el.style.border = '1px solid rgba(255,255,255,0.20)';
    el.style.background = 'rgba(6, 20, 33, 0.92)';
    el.style.boxShadow = '0 10px 30px rgba(0,0,0,0.35)';
    el.style.backdropFilter = 'blur(10px)';
    el.style.webkitBackdropFilter = 'blur(10px)';
    el.style.color = '#fff';
    el.style.fontFamily = 'Arial, sans-serif';
    el.style.fontSize = '14px';
    el.style.lineHeight = '1.25';
    document.body.appendChild(el);
    __hoverEl = el;
    return el;
  }
  function hideHoverEl(){
    if (__hoverEl) __hoverEl.style.display = 'none';
  }
  function setHoverContent(info){
    const el = getHoverEl();
    const name = info?.name || '—';
    const typeLine = ((info?.type || '') + (info?.subtype ? (' — ' + info.subtype) : '')).trim();
    const text = (info?.text || '').trim();
    const note = (info?.note || '').trim();
    const costsRaw = (info && info.costs !== undefined) ? info.costs : [];
    const costs = normalizeCosts(costsRaw);
    // Robust PA extraction (supports multiple JSON shapes)
    let paVal = null;

    const direct = (() => {
      const keys = ['pa','ap','actionpoints','action_points','pointsaction','points_action','pointsdaction','points_daction','pointdaction','point_daction','actioncost','costpa','cost_pa'];
      for (const k of keys) {
        if (info && info[k] !== undefined && info[k] !== null && info[k] !== '') {
          const n = Number(info[k]);
          if (!Number.isNaN(n)) return n;
        }
      }
      return null;
    })();

    

    // Bulle: if JSON uses a single numeric `cost` field, treat it as PA.
    if (paVal === null) {
      const n = Number(costsRaw);
      if (!Number.isNaN(n) && costsRaw !== null && costsRaw !== undefined && String(costsRaw).trim() !== '') {
        paVal = n;
      }
    }
if (direct !== null) {
      paVal = direct;
    } else if (Array.isArray(costsRaw)) {
      const arr = costsRaw;

      const obj = arr.find(c => c && typeof c === 'object' && ['pa','ap','action','actionpoints','pointsdaction','pointdaction'].includes(String(c.key||'').trim().toLowerCase()));
      if (obj && obj.value !== undefined) {
        const n = Number(obj.value);
        if (!Number.isNaN(n)) paVal = n;
      }

      if (paVal === null) {
        for (const it of arr) {
          if (typeof it === 'string') {
            const s = it.trim();
            const mm = s.match(/^\s*(pa|ap|action|actionpoints|pointsdaction|pointdaction)\s*[:=]\s*(-?\d+(?:\.\d+)?)\s*$/i);
            if (mm) { paVal = Number(mm[2]); break; }
          }
        }
      }

      if (paVal === null) {
        const firstNum = arr.find(it => typeof it === 'number' && !Number.isNaN(it));
        if (firstNum !== undefined) paVal = Number(firstNum);
      }
    } else if (costsRaw && typeof costsRaw === 'object') {
      for (const k of Object.keys(costsRaw)) {
        const kk = String(k).trim().toLowerCase();
        if (['pa','ap','action','actionpoints','pointsdaction','pointdaction'].includes(kk)) {
          const n = Number(costsRaw[k]);
          if (!Number.isNaN(n)) { paVal = n; break; }
        }
      }
    }
    const markers = Array.isArray(info?.markers) ? info.markers : [];

    // Small portrait card preview + text header (keeps the pop readable even before illustrations exist)
    const thumbUrlRaw = (info && (info.image || info.img || info.thumbnail || info.cardImageUrl))
      ? (info.image || info.img || info.thumbnail || info.cardImageUrl)
      : PLACEHOLDER_IMG_URL;
    const thumbUrl = escapeHtml(thumbUrlRaw);

    // The large illustration block is the "future card art" slot.
    // For now, we place the placeholder illustration THERE (not in a secondary row).
    const thumbHtml = `<div style="display:flex;gap:12px;align-items:flex-start;margin-bottom:10px;">
      <img src="${thumbUrl}" alt="" style="width:96px;height:136px;border-radius:16px;object-fit:cover;flex:0 0 auto;border:1px solid rgba(255,255,255,0.18);background:rgba(255,255,255,0.06);" />
      <div style="min-width:0;flex:1 1 auto;">
        <div style="font-size:24px;font-weight:800;line-height:1.15;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${escapeHtml(name)}</div>
        <div style="margin-top:4px;font-size:15px;opacity:0.88;">${escapeHtml(typeLine)}</div>
      </div>
    </div>`;

    const costHtml = costs
      .filter(c => c && Number(c.value) > 0 && !['pa','ap','action','actionpoints','pointsdaction','pointdaction'].includes(String(c.key||'').trim().toLowerCase()))
      .slice(0, 8)
      .map(c => {
        const ico = escapeHtml(iconForKey(c.key));
        const rawKey = String(c.key || '').trim();
        const keyLower = rawKey.toLowerCase();
        const label = escapeHtml((keyLower === 'pa' || keyLower === 'action' || keyLower === 'actionpoints' || keyLower === 'ap') ? 'PA' : rawKey);
        const val = Number(c.value);
        const txt = label ? `${ico} ${label} ${val}` : `${ico} ${val}`;
        return `<span style="display:inline-block;margin-right:6px;margin-bottom:6px;padding:3px 9px;border-radius:999px;background:rgba(255,255,255,0.10);border:1px solid rgba(255,255,255,0.12);font-size:12px;white-space:nowrap;">${txt}</span>`;
      })
      .join('');

    const mk = markers
      .filter(m => m && Number(m.value) > 0)
      .slice(0, 10)
      .map(m => {
        const ico = escapeHtml(iconForKey(m.key));
        const label = escapeHtml(String(m.key || '').trim());
        const val = Number(m.value);
        const txt = label ? `${ico} ${label} ${val}` : `${ico} ${val}`;
        return `<span style="display:inline-block;margin-right:6px;margin-bottom:6px;padding:3px 9px;border-radius:999px;background:rgba(255,255,255,0.10);border:1px solid rgba(255,255,255,0.12);font-size:12px;white-space:nowrap;">${txt}</span>`;
      })
      .join('');

    el.innerHTML = `
      ${thumbHtml}
      ${(paVal !== null) ? `<div style="font-weight:800;font-size:18px;margin-bottom:6px;">⚡ ${paVal} PA</div>` : ``}
      ${costHtml ? `<div style="margin:2px 0 10px 0;">${costHtml}</div>` : ''}
      ${text ? `<div style="opacity:0.92;margin-bottom:10px;max-width:400px;">${escapeHtml(text)}</div>` : ''}
      ${note ? `<div style="opacity:0.90;margin-bottom:10px;"><span style="opacity:0.75">📝</span> ${escapeHtml(note)}</div>` : ''}
      ${mk ? `<div style="margin-top:6px">${mk}</div>` : ''}
    `;
    el.style.display = 'block';
    // 🔧 Safety: sur touch, auto-cacher après 2s si le pointerup ne vient pas
    if ('ontouchstart' in window) {
      clearTimeout(el.__autoHideT);
      el.__autoHideT = setTimeout(() => { if (el) el.style.display = 'none'; }, 2000);
    }
    return el;
  }
  function positionHoverEl(clientX, clientY){
    if (!__hoverEl || __hoverEl.style.display === 'none') return;
    const pad = 14;
    const vw = window.innerWidth || 800;
    const vh = window.innerHeight || 600;
    // Measure after content set
    const r = __hoverEl.getBoundingClientRect();
    let x = clientX + 16;
    let y = clientY + 16;
    if (x + r.width + pad > vw) x = Math.max(pad, clientX - r.width - 16);
    if (y + r.height + pad > vh) y = Math.max(pad, clientY - r.height - 16);
    __hoverEl.style.left = `${x}px`;
    __hoverEl.style.top = `${y}px`;
  }
  function escapeHtml(s){
    return String(s).replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  }

  // --- Card database (for names/types/text) ---
  // app.js keeps cardsById inside an IIFE; we load the same /data/cards.json here (read-only)
  let __cardsById = null; // Map('003' -> cardObj)
  let __cardsLoadPromise = null;

  function normCardNumber(raw){
    const s = (raw == null) ? '' : String(raw).trim();
    const m = s.match(/(\d+)/);
    return m ? String(m[1]).padStart(3,'0') : s;
  }

  async function ensureCardsLoaded(){
    try{
      if (__cardsById) return __cardsById;
      if (__cardsLoadPromise) return __cardsLoadPromise;
      __cardsLoadPromise = (async ()=>{
        const url = `/data/cards.json?v=${Date.now()}`;
        const res = await fetch(url, { cache: 'no-store' });
        if(!res.ok) throw new Error(`HTTP ${res.status} on ${url}`);
        const j = await res.json();
        const arr = Array.isArray(j) ? j : (j && Array.isArray(j.cards) ? j.cards : null);
        const map = new Map();
        if(arr){
          for(const c of arr){
            const id = normCardNumber(c.number ?? c.id ?? c.collectorNumber ?? c.cardId ?? c.card_id);
            if(!id) continue;
            map.set(id, c);
          }
        }
        __cardsById = map;
        // Load all artworks before first render so we can show true miniatures
        // (not a black/white placeholder).
        await preloadAllCardArt(map);
        if (DEBUG) console.log('[Pixi] cards.json loaded', map.size);
        return map;
      })();
      return __cardsLoadPromise;
    }catch(e){
      if (DEBUG) console.warn('[Pixi] cards.json load failed', e);
      __cardsLoadPromise = null;
      return null;
    }
  }

  function lookupCardByIdHint(idHint){
    if(!idHint || !__cardsById) return null;
    const id = normCardNumber(idHint);
    return __cardsById.get(id) || null;
  }

  const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

  function normZoneId(zoneId){
    const map = {
      myTerrain: 'myTerrainZone',
      oppTerrain: 'oppTerrainZone',
      myTerrainZone: 'myTerrainZone',
      oppTerrainZone: 'oppTerrainZone',
    };
    return map[zoneId] || zoneId;
  }

  function pick(obj, keys, fallback=''){
    if (!obj) return fallback;
    for (const k of keys){
      if (obj[k] !== undefined && obj[k] !== null && obj[k] !== '') return obj[k];
    }
    return fallback;
  }

  function normalizeTerrain(input){
    // Accept array, object map, or wrapped structures.
    if (!input) return [];
    if (Array.isArray(input)) return input;
    if (typeof input !== 'object') return [];

    // Common wrappers
    const commonKeys = ['items','cards','creatures','tokens','terrain','list','arr','array','slots'];
    for (const k of commonKeys){
      const v = input[k];
      if (Array.isArray(v)) return v;
    }

    // If it's a map/object of entries, try values.
    const vals = Object.values(input);
    // If any value is an array, pick the largest (most likely the list)
    let bestArr = null;
    for (const v of vals){
      if (Array.isArray(v) && (!bestArr || v.length > bestArr.length)) bestArr = v;
    }
    if (bestArr) return bestArr;

    // Otherwise, if values look like card instances, return them (filter out primitives)
    const objVals = vals.filter(v => v && typeof v === 'object');
    return objVals;
  }

  function debugTerrainShape(input){
    if (!DEBUG) return;
    try{
      if (Array.isArray(input)) return { kind:'array', len: input.length };
      if (!input || typeof input !== 'object') return { kind: typeof input };
      const keys = Object.keys(input).slice(0, 20);
      const best = normalizeTerrain(input);
      return { kind:'object', keys, normalizedLen: best.length };
    }catch(e){ return { kind:'error' }; }
  }

  function toStr(v){
    if (v === undefined || v === null) return '';
    if (typeof v === 'string') return v;
    try { return String(v); } catch { return ''; }
  }

  function isTruthy(v){
    return v === true || v === 1 || v === '1' || v === 'true' || v === 'yes' || v === 'oui';
  }

  function detectTapped(raw){
    if (!raw) return false;
    // Common flags / keys
    const keys = [
      'tapped','isTapped',
      'exhausted','isExhausted',
      'couche','couchee','couchée','isCouche','isCouché','isCouchee',
      'incline','inclinee','incliné','inclined',
      'sleep','sleeping',
      'rest','resting'
    ];
    for (const k of keys){
      if (raw[k] !== undefined) return isTruthy(raw[k]);
    }
    const st = toStr(raw.state || raw.position || raw.status).toLowerCase();
    if (st === 'tapped' || st === 'exhausted' || st === 'couche' || st === 'couché' || st === 'couchee' || st === 'inclined') return true;
    return false;
  }

  function normalizeMarkers(v){
    // Accept array [{key,value}] or object {key:value}
    // Keep only meaningful markers (value > 0)
    if (!v) return [];
    const out = [];
    if (Array.isArray(v)){
      for (const x of v){
        if (!x) continue;
        if (typeof x === 'string'){
          out.push({ key: x, value: 1 });
          continue;
        }
        if (typeof x === 'number'){
          if (x > 0) out.push({ key: 'mk', value: x });
          continue;
        }
        if (typeof x === 'object'){
          const key = toStr(pick(x, ['key','type','id','name','label'], ''));
          const rawVal = pick(x, ['value','count','n','qty','amount'], 0);
          const value = Number(rawVal);
          if (key && Number.isFinite(value) && value > 0) out.push({ key, value });
        }
      }
      return out;
    }
    if (typeof v === 'object'){
      for (const [k, rawVal] of Object.entries(v)){
        // Ignore nested objects/arrays (likely not marker counts)
        if (rawVal && typeof rawVal === 'object') continue;
        const value = Number(rawVal);
        if (Number.isFinite(value) && value > 0){
          out.push({ key: toStr(k), value });
        }
      }
      return out;
    }
    return [];
  }

  function normalizeCosts(v){
    // Costs are PA (points d'action) in Bulle.
    // Accept array [{key,value}] or object {key:value} or number/string.
    // Keep only meaningful costs (value > 0) and normalize the key to 'PA' when relevant.
    if (v === undefined || v === null) return [];
    const out = [];

    const normKey = (k) => {
      const kk = toStr(k).toLowerCase();
      if (!kk) return 'PA';
      if (kk === 'pa' || kk.includes('point') || kk.includes('action') || kk.includes('ap')) return 'PA';
      if (kk.includes('energie') || kk.includes('energy') || kk.includes('light')) return 'PA';
      return String(k).toUpperCase();
    };

    const push = (k, rawVal) => {
      const value = Number(rawVal);
      if (Number.isFinite(value) && value > 0){
        out.push({ key: normKey(k), value });
      }
    };

    if (typeof v === 'number'){ push('PA', v); return out; }
    if (typeof v === 'string'){
      const n = Number(v);
      if (Number.isFinite(n)) push('PA', n);
      return out;
    }
    if (Array.isArray(v)){
      for (const x of v){
        if (!x) continue;
        if (typeof x === 'string'){ push('PA', 1); continue; }
        if (typeof x === 'number'){ push('PA', x); continue; }
        if (typeof x === 'object'){
          const key = toStr(pick(x, ['key','type','id','name','label'], 'PA')) || 'PA';
          const rawVal = pick(x, ['value','count','n','qty','amount'], 0);
          push(key, rawVal);
        }
      }
      return out;
    }
    if (typeof v === 'object'){
      for (const [k, rawVal] of Object.entries(v)){
        if (rawVal && typeof rawVal === 'object') continue;
        push(k, rawVal);
      }
      return out;
    }
    return [];
  }

  function iconForKey(k){
    const key = toStr(k).toLowerCase();
    // PA (points d'action)
    if (key === 'pa' || key.includes('action') || key.includes('point') || key.includes('ap')) return '⚡';
    // Optional other cost icons (if you add later)
    if (key.includes('time') || key.includes('chrono') || key.includes('clock') || key.includes('timer')) return '⏱';
    if (key.includes('mana')) return '◇';
    // Markers
    if (key.includes('shield') || key.includes('arm')) return '🛡';
    if (key.includes('poison') || key.includes('toxi')) return '☠';
    if (key.includes('burn') || key.includes('fire')) return '🔥';
    if (key.includes('freeze') || key.includes('ice')) return '❄';
    if (key.includes('stun')) return '💫';
    if (key.includes('note')) return '📝';
    return '•';
  }

  function extractCardInfo(k){
    const key = toStr(k).toLowerCase();
    // Cost icons (adapt as your game evolves)
    if (key.includes('energie') || key.includes('energy') || key.includes('elec') || key.includes('light')) return '⚡';
    if (key.includes('time') || key.includes('chrono') || key.includes('clock') || key.includes('timer')) return '⏱';
    if (key === 'pa' || key.includes('action')) return '●';
    if (key.includes('mana')) return '◇';
    // Markers
    if (key.includes('shield') || key.includes('arm')) return '🛡';
    if (key.includes('poison') || key.includes('toxi')) return '☠';
    if (key.includes('burn') || key.includes('fire')) return '🔥';
    if (key.includes('freeze') || key.includes('ice')) return '❄';
    if (key.includes('stun')) return '💫';
    if (key.includes('note')) return '📝';
    return '•';
  }

  function extractCardInfo(item){
  // Support tokens stored as strings (e.g. "iid::cid") or raw ids.
  if(typeof item === 'string' || typeof item === 'number'){
    const s = String(item);
    const m = s.match(/(\d{1,3})/);
    const id = m ? String(m[1]).padStart(3,'0') : s;
    const resolved = lookupCardByIdHint(id);
    const name = resolved ? (resolved.name || resolved.title || resolved.nom || `Carte ${id}`) : `Carte ${id}`;
    const type = resolved ? (resolved.type || resolved.kind || '') : '';
    const text = resolved ? (resolved.text || resolved.description || resolved.desc || '') : '';
    const imgSrc = resolved ? (resolved.image || resolved.img || resolved.art || resolved.illustration || resolved.illustrationUrl || '') : '';
    const image = imgSrc ? resolveAssetUrl(imgSrc) : null;
    return {id, name, type, text, image};
  }

    const src =
      item?.card || item?.cardData || item?.base || item?.template || item?.def || item?.data || item?.meta || {};
        const idHint = toStr(pick(item, ['cardId','card_id','id','uid','instId','instanceId','refId'], '')) || toStr(pick(src, ['cardId','card_id','id'], ''));
        const resolved = (idHint ? lookupCardByIdHint(idHint) : null);
    const resolvedName = toStr(pick(resolved, ['name','title','nom'], ''));
    const name = toStr(pick(item, ['name','cardName','title','nom'], '')) || toStr(pick(src, ['name','title','nom'], '')) || resolvedName || (idHint ? idHint : '—');
        const type = toStr(pick(item, ['type','cardType','kind'], '')) || toStr(pick(src, ['type','cardType','kind'], '')) || toStr(pick(resolved, ['type','cardType','kind'], ''));
    const subtype = toStr(pick(item, ['subtype','subType','race','family','tribe','sousType','sous_type'], '')) ||
                    toStr(pick(src, ['subtype','subType','race','family','tribe','sousType','sous_type'], ''));
        const text = toStr(pick(item, ['text','rules','rulesText','effect','desc','description','texte'], '')) ||
                 toStr(pick(src, ['text','rules','rulesText','effect','desc','description','texte'], '')) ||
                 toStr(pick(resolved, ['text','rules','rulesText','effect','desc','description','texte'], ''));
    const note = toStr(pick(item, ['note','playerNote','memo','annotation','comment'], '')) ||
                 toStr(pick(src, ['note','playerNote','memo','annotation','comment'], ''));
    const costsRaw = pick(item, ['cost','costs','cout','coût','manaCost'], null) ?? pick(src, ['cost','costs','cout','coût','manaCost'], null) ?? pick(resolved, ['cost','costs','cout','coût','manaCost'], null);
    const markersRaw = pick(item, ['markers','counters','counter','tokens','token','marqueurs'], null) ??
                       pick(src,  ['markers','counters','counter','tokens','token','marqueurs'], null) ??
                       pick(resolved, ['markers','counters','counter','tokens','token','marqueurs'], null);
    const costs = normalizeCosts(costsRaw);
    const markers = normalizeMarkers(markersRaw);
    const tapped = detectTapped(item) || detectTapped(src);

    // Optional stats (if exist)
    const atk = pick(item, ['atk','attack','att','puissance'], undefined) ?? pick(src, ['atk','attack','att','puissance'], undefined);
    const hp  = pick(item, ['hp','health','pv','vie'], undefined) ?? pick(src, ['hp','health','pv','vie'], undefined);

    // Future full-card image path (optional)
    // Convention: /assets/cards/<number>.webp  (or .png). You can start by exporting placeholders.
    let cardImageUrl = '';
    try{
      const img = (src && src.image) || (resolved && resolved.image) || '';
      if (typeof img === 'string') cardImageUrl = img;
    }catch(e){}
    // If we don't have a real image, keep it empty so we don't fetch a missing placeholder asset.
    if(!cardImageUrl){
      cardImageUrl = '';
    }

    // Normalized field name used by hover + sprites
    const image = cardImageUrl;

    return { name, type, subtype, text, note, costs, markers, tapped, atk, hp, image, cardImageUrl, __raw: item };
  }

  function measureTextHeight(fontSize, lines){
    return Math.ceil(fontSize * 1.2 * lines);
  }

  
  function truncateToFit(text, style, maxWidth){
    if (!text) return '';
    // fast path
    let t = String(text);
    // Measure with a temporary Text
    const tmp = new PIXI.Text({ text: t, style });
    if (tmp.width <= maxWidth) return t;
    // Binary-ish shrink
    const ell = '…';
    let lo = 0, hi = t.length;
    // ensure at least 1 char
    while (lo < hi){
      const mid = Math.floor((lo + hi) / 2);
      const cand = t.slice(0, Math.max(1, mid)) + ell;
      tmp.text = cand;
      if (tmp.width <= maxWidth) lo = mid + 1;
      else hi = mid;
    }
    const cut = Math.max(1, hi - 1);
    const out = t.slice(0, cut) + ell;
    tmp.destroy?.();
    return out;
  }

function createPill(app, label, w, h, alpha=0.55){
    const g = new PIXI.Graphics();
    g.roundRect(0, 0, w, h, Math.min(10, h/2));
    g.fill({ color: 0x000000, alpha: alpha });
    g.stroke({ color: 0xffffff, alpha: 0.16, width: 1 });
    const t = new PIXI.Text({
      text: label,
      style: { fill: 0xffffff, fontSize: Math.max(10, Math.floor(h*0.55)), fontFamily: 'Arial', fontWeight: '600' }
    });
    t.alpha = 0.92;
    t.x = Math.floor((w - t.width) / 2);
    t.y = Math.floor((h - t.height) / 2) - 1;
    const c = new PIXI.Container();
    c.addChild(g, t);
    return c;
  }

  function buildCardContainer(info, w, h, opts){
    const { isPop=false } = opts || {};
    // Minimal tile mode: keep it tiny and clean (only 1–2 letters).
    if (!isPop){
      const root = new PIXI.Container();
      const radius = Math.min(14, Math.floor(Math.min(w,h) * 0.40));

      // Soft shadow (premium readability) - effet 3D amélioré
      const sh = new PIXI.Graphics();
      sh.roundRect(3, 4, w, h, radius); // Décalage plus prononcé pour effet de profondeur
      sh.fill({ color: 0x000000, alpha: 0.35 }); // Alpha augmenté pour plus de contraste
      root.addChild(sh);

      // Ombre secondaire pour effet de profondeur
      const sh2 = new PIXI.Graphics();
      sh2.roundRect(1.5, 2, w, h, radius);
      sh2.fill({ color: 0x000000, alpha: 0.15 });
      root.addChild(sh2);

      const bg = new PIXI.Graphics();
      bg.roundRect(0, 0, w, h, radius);
      // Tapped cards are slightly dimmer to make the state obvious even at tiny size
      const tapped = !!info?.tapped;
      bg.fill({ color: 0x0b2238, alpha: tapped ? 0.32 : 0.44 });
      bg.stroke({ color: 0xffffff, alpha: tapped ? 0.14 : 0.18, width: 1.35 });
      root.addChild(bg);

      // Highlight pour effet de lumière (rendu 3D)
      const highlight = new PIXI.Graphics();
      highlight.roundRect(1, 1, w - 2, h/3, radius);
      highlight.fill({ color: 0xffffff, alpha: tapped ? 0.03 : 0.08 });
      root.addChild(highlight);

      // Miniature = full art (fallback to placeholder) — load safely (no black squares)
      const rawArtUrl = (info && (info.image || info.cardImageUrl || info.img || info.thumbnail))
        ? String(info.image || info.cardImageUrl || info.img || info.thumbnail)
        : '';
      const cands = resolveArtUrlCandidates(rawArtUrl);
      const placeholderTex = getPlaceholderTexture();
      try{
        const spr = new PIXI.Sprite(placeholderTex);
        spr.alpha = tapped ? 0.72 : 0.98;
        spr.x = w/2;
        spr.y = h/2;
        spr.anchor.set(0.5);
        spr.width = w;
        spr.height = h;

        // Mask to rounded rect so it stays inside the tile
        const m = new PIXI.Graphics();
        m.roundRect(0, 0, w, h, radius);
        m.fill({ color: 0xffffff, alpha: 1 });
        spr.mask = m;

        root.addChild(spr);
        root.addChild(m);

        // Try candidates in order; prefer preloaded textures so the miniature is visible immediately.
        for (const url of cands){
          if (!url || url === PLACEHOLDER_IMG_URL) continue;
          const tex = __artTexCache.get(url) || getArtTexture(url);
          if (!tex || !tex.baseTexture) continue;
          // If we preloaded, it's already valid; otherwise we still accept it.
          spr.texture = tex;
          try{ spr.texture.baseTexture.scaleMode = PIXI.SCALE_MODES.LINEAR; }catch(e){}
          break;
        }
      }catch(e){}

      // A tiny diagonal slash when tapped (subtle but readable)
      if (tapped){
        const slash = new PIXI.Graphics();
        slash.moveTo(6, h-6);
        slash.lineTo(w-6, 6);
        slash.stroke({ color: 0xffffff, alpha: 0.22, width: Math.max(1.2, Math.min(2.0, w*0.06)) });
        root.addChild(slash);
      }
      return root;
    }

    const pad = isPop ? 14 : 8;
    const headerH = isPop ? 56 : 34;
    const costH = isPop ? 22 : 16;
    const fontTitle = isPop ? 22 : 12;
    const fontType  = isPop ? 16 : 10;
    const fontBody  = isPop ? 15 : 9;
    const radius = isPop ? 18 : 12;

    const root = new PIXI.Container();
    root.sortableChildren = true;

    // Background panel
    const bg = new PIXI.Graphics();
    bg.roundRect(0, 0, w, h, radius);
    bg.fill({ color: 0x0b2238, alpha: 0.50 });
    bg.stroke({ color: 0xffffff, alpha: 0.18, width: 1.25 });
    root.addChild(bg);

    // Inner glossy highlight (fake lighting)
    const gloss = new PIXI.Graphics();
    gloss.roundRect(0, 0, w, Math.floor(h*0.42), radius);
    gloss.fill({ color: 0xffffff, alpha: 0.06 });
    gloss.alpha = 0.65;
    root.addChild(gloss);
    root.__gloss = gloss;

    // Title
    const title = new PIXI.Text({
      text: info.name || '—',
      style: {
        fill: 0xffffff,
        fontSize: fontTitle,
        fontFamily: 'Arial',
        fontWeight: '700',
        wordWrap: isPop,
        wordWrapWidth: w - pad*2,
      }
    });
    title.alpha = 0.96;
    title.x = pad;
    title.y = pad + (isPop ? 2 : 0);
    
    // Ensure tiny tiles keep a single readable line
    if (!isPop){
      const maxW = Math.max(10, w - pad*2);
      title.text = truncateToFit(title.text, title.style, maxW);
    }
root.addChild(title);

    // Type/Subtype line
    const typeLine = ((info.type || '') + (info.subtype ? (' — ' + info.subtype) : '')).trim();
    const typ = new PIXI.Text({
      text: typeLine || '',
      style: {
        fill: 0xd6e6ff,
        fontSize: fontType,
        fontFamily: 'Arial',
        fontWeight: '600',
        wordWrap: isPop,
        wordWrapWidth: w - pad*2,
      }
    });
    typ.alpha = 0.70;
    typ.x = pad;
    typ.y = pad + title.height + (isPop ? 6 : 2);
    root.addChild(typ);

    // Costs row
    const costs = info.costs || [];
    let cx = pad;
    let cy = pad + headerH;
    const pillGap = 6;
    const pillH = costH;
    for (let i=0; i<Math.min(4, costs.length); i++){
      const c = costs[i];
      const label = `${iconForKey(c.key)} ${c.value}`;
      const pillW = Math.max(34, Math.min(70, Math.floor(label.length * (isPop ? 10 : 7))));
      const pill = createPill(null, label, pillW, pillH, 0.35);
      pill.x = cx;
      pill.y = cy;
      root.addChild(pill);
      cx += pillW + pillGap;
    }

    // Optional stats (ATK/HP)
    if (info.atk !== undefined || info.hp !== undefined){
      const a = (info.atk !== undefined) ? Number(info.atk) : null;
      const b = (info.hp  !== undefined) ? Number(info.hp)  : null;
      const statLabel = `${a !== null && !Number.isNaN(a) ? a : '—'} / ${b !== null && !Number.isNaN(b) ? b : '—'}`;
      const stat = createPill(null, statLabel, isPop ? 76 : 54, pillH, 0.25);
      stat.x = w - pad - stat.width;
      stat.y = cy;
      root.addChild(stat);
    }

    // Body text area (with panel)
    const bodyY = cy + pillH + (isPop ? 12 : 8);
    const bodyH = h - bodyY - pad - (isPop ? 34 : 22);
    const bodyPanel = new PIXI.Graphics();
    bodyPanel.roundRect(pad, bodyY, w - pad*2, Math.max(24, bodyH), isPop ? 14 : 10);
    bodyPanel.fill({ color: 0x000000, alpha: 0.18 });
    bodyPanel.stroke({ color: 0xffffff, alpha: 0.10, width: 1 });
    root.addChild(bodyPanel);

    const bodyText = new PIXI.Text({
      text: info.text || '',
      style: {
        fill: 0xeaf3ff,
        fontSize: fontBody,
        fontFamily: 'Arial',
        fontWeight: '500',
        wordWrap: isPop,
        wordWrapWidth: (w - pad*2) - (isPop ? 18 : 12),
        lineHeight: Math.ceil(fontBody*1.25),
      }
    });
    bodyText.alpha = 0.82;
    bodyText.x = pad + (isPop ? 10 : 6);
    bodyText.y = bodyY + (isPop ? 10 : 7);

    // In mini mode: cap to ~3 lines visually by trimming text length (cheap & fast)
    if (!isPop && bodyText.text.length > 120){
      bodyText.text = bodyText.text.slice(0, 118) + '…';
    }
    root.addChild(bodyText);

    // Footer markers + note
    const footerY = h - pad - (isPop ? 24 : 18);

    const markers = info.markers || [];
    let mx = pad;
    for (let i=0; i<Math.min(5, markers.length); i++){
      const m = markers[i];
      const label = `${iconForKey(m.key)}${m.value}`;
      const pillW = Math.max(26, Math.min(60, Math.floor(label.length * (isPop ? 9 : 7))));
      const pill = createPill(null, label, pillW, isPop ? 18 : 14, 0.25);
      pill.x = mx;
      pill.y = footerY;
      pill.alpha = 0.9;
      root.addChild(pill);
      mx += pillW + 6;
    }

    if (info.note){
      const noteBadge = createPill(null, '📝', isPop ? 32 : 22, isPop ? 18 : 14, 0.25);
      noteBadge.x = w - pad - noteBadge.width;
      noteBadge.y = footerY;
      root.addChild(noteBadge);

      if (isPop){
        const noteText = new PIXI.Text({
          text: info.note,
          style: {
            fill: 0xffffff,
            fontSize: 13,
            fontFamily: 'Arial',
            fontWeight: '500',
            wordWrap: isPop,
            wordWrapWidth: w - pad*2,
            lineHeight: 16,
          }
        });
        noteText.alpha = 0.78;
        noteText.x = pad;
        noteText.y = footerY - 8 - Math.min(48, noteText.height);
        root.addChild(noteText);
      }
    }

    // Couché / Debout visual
    if (info.tapped){
      const tapBadge = createPill(null, '⤵︎', isPop ? 34 : 24, isPop ? 18 : 14, 0.22);
      tapBadge.x = w - pad - tapBadge.width;
      tapBadge.y = pad;
      tapBadge.alpha = 0.9;
      root.addChild(tapBadge);

      // Slight dim
      bg.alpha = 0.42;
      bodyText.alpha = 0.72;
    }

    root.__bg = bg;
    root.__info = info;
    return root;
  }

  class ZoneRenderer {
    constructor(containerEl){
      this.containerEl = containerEl;
      this.zoneEl = containerEl;
      this.app = null;
      this.overlay = null;
      this.header = null;

      this.cardsLayer = null;
      this.popLayer = null;

      this.items = []; // { rect:{x,y,w,h}, info, tapped, x,y,w,h }
      this.hoverIndex = -1;
      this.popActive = false;
      this.popPinned = false; // on mobile tap
      this.popCard = null;
      this.popTarget = { x:0, y:0, s:1 };
      this.popAnim = { x:0, y:0, s:1, v:0 };

      // Hover preview timing (avoid popup spam on quick mouse pass)
      this.hoverDelayMs = (window.PREMIUM_CONFIG && window.PREMIUM_CONFIG.timings && window.PREMIUM_CONFIG.timings.hoverPreviewDelayMs) || 320;
      this.hoverHideDelayMs = (window.PREMIUM_CONFIG && window.PREMIUM_CONFIG.timings && window.PREMIUM_CONFIG.timings.hoverPreviewHideDelayMs) || 120;
      this._hoverTimer = null;
      this._hideTimer = null;
      this._lastPointer = null;

      // Internal vertical scroll for crowded zones (Option 3)
      this.scrollY = 0;
      this.scrollMax = 0;
      this.scrollEnabled = false;
      this._scrollDragActive = false;


      this._lastLogTs = 0;
    }

    async init(){
      const overlay = document.createElement('div');
      overlay.className = 'pixi-overlay';
      overlay.style.position = 'absolute';
      overlay.style.inset = '0';
      overlay.style.pointerEvents = 'none'; // DO NOT block DOM; we do hit-tests from document events
      overlay.style.zIndex = '3';
      if (!this.containerEl.style.position) this.containerEl.style.position = 'relative';
      this.containerEl.appendChild(overlay);
      this.overlay = overlay;

      const app = new PIXI.Application();
      await app.init({
        resizeTo: overlay,
        backgroundAlpha: 0,
        antialias: true,
        autoDensity: true,
        resolution: Math.min(window.devicePixelRatio || 1, 2),
        preferWebGLVersion: 2,       // WebGL2 is more stable on Firefox
        powerPreference: 'default',  // avoid aggressive GPU usage that triggers context loss
      });
      overlay.appendChild(app.canvas);
      this.app = app;

      // Best-effort handlers: certains mobiles peuvent perdre le contexte WebGL (onglet, mémoire GPU)
      try{
        const view = app.canvas || app.view || app.renderer?.view;
        if(view && view.addEventListener){
          view.addEventListener('webglcontextlost', (e)=>{
            try{ e.preventDefault(); }catch(_e){}
            console.warn('[Pixi] WebGL context lost');
            this.__webglLost = true;
            window.__pixiWebglLost = true;
          }, false);

          view.addEventListener('webglcontextrestored', ()=>{
            console.warn('[Pixi] WebGL context restored — re-rendering zones');
            this.__webglLost = false;
            window.__pixiWebglLost = false;
            // Re-render all zones with their last known terrain data
            setTimeout(()=>{
              try{
                for(const [key, zr] of this.zones){
                  if(zr.__lastTerrain) zr.render(zr.__lastTerrain);
                }
              }catch(_e){ console.warn('[Pixi] re-render after context restore failed:', _e); }
            }, 200);
          }, false);
        }
      }catch(_e){}

      
      // Exposer l'app globalement pour les PostFX
      window.__pixiApp = app;

      if (DEBUG){
        overlay.style.outline = '1px dashed rgba(255,255,255,0.22)';
        overlay.style.borderRadius = '16px';
      }

      this.cardsLayer = new PIXI.Container();
      this.cardsLayer.sortableChildren = true;
      app.stage.addChild(this.cardsLayer);

      this.popLayer = new PIXI.Container();
      this.popLayer.sortableChildren = true;
      app.stage.addChild(this.popLayer);

      const header = new PIXI.Text({
        text: 'Pixi: 0',
        style: { fill: 0xffffff, fontSize: 14, fontFamily: 'Arial', dropShadow: true, dropShadowBlur: 4, dropShadowDistance: 0 }
      });
      header.alpha = DEBUG ? 0.85 : 0.0;
      header.x = 12; header.y = 10;
      app.stage.addChild(header);
      this.header = header;

      // Animation ticker for pop-out smoothness + gloss sweep
      app.ticker.add(() => this.tick());
    }

    screenFromClient(clientX, clientY){
      if (!this.app || !this.app.canvas) return null;
      const rect = this.app.canvas.getBoundingClientRect();
      if (clientX < rect.left || clientX > rect.right || clientY < rect.top || clientY > rect.bottom) return null;
      const sx = (clientX - rect.left) * (this.app.screen.width / rect.width);
      const sy = (clientY - rect.top) * (this.app.screen.height / rect.height);
      return { x: sx, y: sy, rect };
    }

    clear(){
      if (!this.cardsLayer) return;
      const children = this.cardsLayer.removeChildren();
      for (const ch of children) ch.destroy?.({ children: true });
      this.items = [];
      this.hoverIndex = -1;
      // keep popLayer content; it is managed separately
    }

    render(terrainArr){
      if (!this.app || !this.cardsLayer) return;
      if (window.__pixiWebglLost) return; // skip rendering during context loss
      this.__lastTerrain = terrainArr; // cache for WebGL context restore
      const arr = normalizeTerrain(terrainArr);
      const count = arr.length;
      // Détecte enfant mode tôt (utilisé pour scroll + layout)
      const _isEnfant = (typeof window.GAME_MODE !== 'undefined' && window.GAME_MODE === 'enfant')
        || (typeof window.roomState !== 'undefined' && window.roomState?.gameMode === 'enfant');
      // Mode enfant : scroll uniquement si > 10 créatures (2 rangées de 5)
      this.scrollEnabled = _isEnfant ? (count > 10) : (count >= 4);

      if (this.header) this.header.text = `Pixi: ${count}`;

      this.clear();

      const W = this.app.screen.width;
      const H = this.app.screen.height;
      if (!W || !H) return;


      // Layout grid tuned for Bulle terrain: zones are short and the usable “slots” area is narrow.
      // We keep cards small (close to the quick-action buttons size) and stick them to the left,
      // so we don't overlap the center counter/label or the right quick actions.
      
const gap = _isEnfant ? 5 : 8;
const topPad = 6;

// ---- CALQUE UI (terrain-zone) ----
// Instead of guessing reserved widths, we measure the DOM parts:
// - right quick-actions block
// - centered placeholder (counter + label)
// Then we only draw in the left free strip, so it matches the UI.
let usableLeftW = W - 20; // fallback : pleine largeur si pas de contraintes DOM
try{
  const zoneRect = this.zoneEl?.getBoundingClientRect?.();
  if (zoneRect){
    // Rects in CSS pixels (DOM). We'll convert to Pixi screen coordinates.
    const qa = this.zoneEl.querySelector?.('.terrain-quick-actions');
    const ph = this.zoneEl.querySelector?.('.terrain-placeholder');
    const qaRect = qa?.getBoundingClientRect?.();
    const phRect = ph?.getBoundingClientRect?.();

    // Convert DOM px to Pixi screen units (accounting for resolution scaling)
    const sx = this.app.screen.width / zoneRect.width;
    const domToScreenX = (x) => (x - zoneRect.left) * sx;

    const candidates = [];
    if (qaRect) candidates.push(domToScreenX(qaRect.left) - gap);
    if (phRect) candidates.push(domToScreenX(phRect.left) - gap);
    if (candidates.length){
      usableLeftW = clamp(Math.min(...candidates), 60, W - 20);
    } else {
      // Aucun élément placeholder/quick-actions : utiliser toute la largeur (ex: mode enfant)
      usableLeftW = clamp(W - 20, 60, W - 20);
    }
  }
}catch(e){
  usableLeftW = clamp(W - 20, 60, W - 20);
}

const startX = 10;
const usableW = Math.max(0, usableLeftW - startX);

// Terrain zones are VERY short: size is driven by height, but we cap width strongly
// (user wants tiles close to the quick-action buttons).
const availH = Math.max(0, H - topPad - gap*2);
const baseH = clamp(Math.floor(availH * 0.78), 24, 54);
const baseW = clamp(Math.floor(baseH * 0.85), 24, 44);

// ── MODE ENFANT : 5 cartes par rangée, art plein — utilise toute la largeur ──
const isEnfantPixi = _isEnfant;

// Pick columns that fit in the left usable area (keep small tiles).
const maxCols = 8;
let cols, cardW;

if (isEnfantPixi) {
  // Cible : min(5, count) colonnes, cartes étalées sur toute la largeur
  cols = Math.max(1, Math.min(5, count));
  cardW = Math.max(20, Math.floor((usableW - gap * (cols + 1)) / cols));
  // Cap : une carte unique ne doit pas devenir un banner
  const maxEnfantW = Math.max(80, Math.floor((usableW - gap * 6) / 5));
  if (cardW > maxEnfantW) {
    cardW = maxEnfantW;
    // Recomputer cols pour rester centré
    cols = Math.max(1, Math.min(5, count));
  }
} else {
  cols = Math.max(1, Math.min(maxCols, count, Math.floor((usableW - gap) / (baseW + gap)) || 1));
  // Compute size from usableW/cols (do NOT grow beyond baseW)
  cardW = clamp(Math.floor((usableW - gap*(cols+1)) / cols), 20, baseW);
  while (cols > 1 && cardW < 22){
    cols -= 1;
    cardW = clamp(Math.floor((usableW - gap*(cols+1)) / cols), 20, baseW);
  }
}

let rows = Math.max(1, Math.ceil(count / cols));
let cardH;
if (isEnfantPixi) {
  // Mode enfant : remplir la hauteur disponible (pas de cap artificiel)
  cardH = clamp(Math.floor((availH - gap*(rows-1)) / rows), 24, availH);
} else if (this.scrollEnabled){
  // Keep tiles readable; when crowded, enable internal scroll instead of shrinking to nothing.
  cardH = baseH;
} else {
  cardH = clamp(Math.floor((availH - gap*(rows-1)) / rows), 20, baseH);
}

// 📐 Ratio portrait : les cartes doivent rester plus hautes que larges.
// Sans cette contrainte, en 2 rangées sur mobile le cardH < cardW → distortion horizontale.
// Ratio cible 3:4 (0.75 W/H) — correspond aux images de cartes.
if (isEnfantPixi) {
  const MAX_W_RATIO = 0.75; // cardW <= cardH * 0.75
  if (cardW > cardH * MAX_W_RATIO) {
    cardW = Math.max(16, Math.floor(cardH * MAX_W_RATIO));
  }
}

if (DEBUG){
  try{
    console.log('[Pixi] layout', { W, H, usableLeftW, usableW, cols, rows, cardW, cardH, count });
  }catch(e){}
}

// Update scroll bounds (screen coords). Items are stored in content coordinates; hit-tests add scrollY.
if (this.scrollEnabled){
  const contentH = topPad + gap + rows*(cardH + gap) - gap; // no trailing gap
  this.scrollMax = Math.max(0, contentH - H);
  this.scrollY = clamp(this.scrollY, 0, this.scrollMax);
} else {
  this.scrollMax = 0;
  this.scrollY = 0;
}
if (this.cardsLayer) this.cardsLayer.y = -this.scrollY;




      // Mode enfant : centrer horizontalement si la rangée ne remplit pas toute la largeur
      const centerOffX = isEnfantPixi
        ? Math.max(0, Math.floor((usableW - (cols * cardW + (cols - 1) * gap)) / 2))
        : 0;

      for (let i=0; i<count; i++){
        const raw = arr[i] || {};
        const info = extractCardInfo(raw);

        const col = i % cols;
        const row = Math.floor(i / cols);
        let x = startX + centerOffX + col*(cardW + gap);
        let y = topPad + gap + row*(cardH + gap);

        // Small card container
        const c = buildCardContainer(info, cardW, cardH, { isPop: false });

        // Couché rotation (TCG style)
        let rectW = cardW, rectH = cardH;
        if (info.tapped){
          c.rotation = Math.PI / 2;
          // rotate around top-left -> we want it to stay within its cell
          // After rotation, width/height swap. We'll place to center within cell.
          rectW = cardH;
          rectH = cardW;
          const cellCx = x + cardW/2;
          const cellCy = y + cardH/2;
          c.pivot.set(cardW/2, cardH/2);
          c.position.set(cellCx, cellCy);
        } else {
          c.x = x;
          c.y = y;
        }

        // Slight depth ordering
        c.zIndex = 1 + i;

        this.cardsLayer.addChild(c);

        // Store hit rect in screen coordinates
        let rx, ry;
        if (info.tapped){
          rx = x + (cardW - rectW)/2;
          ry = y + (cardH - rectH)/2;
        } else {
          rx = x; ry = y;
        }
        this.items.push({ rect: { x: rx, y: ry, w: rectW, h: rectH }, info, raw });
      }
    }

    // Hit test in screen coords
    hitTest(sx, sy){
      const cy = sy + (this.scrollEnabled ? this.scrollY : 0);
      for (let i=0; i<this.items.length; i++){
        const r = this.items[i].rect;
        if (sx >= r.x && sx <= (r.x + r.w) && cy >= r.y && cy <= (r.y + r.h)) return i;
      }
      return -1;
    }

    setScroll(y){
      const ny = clamp(y, 0, this.scrollMax || 0);
      if (ny === this.scrollY) return;
      this.scrollY = ny;
      if (this.cardsLayer) this.cardsLayer.y = -this.scrollY;
      // While scrolling, don't keep a popout open (avoids visual jitter)
      if (this.popPinned) return;
      this.setHover(-1, null);
    }

    setHover(index, pointer){
      // If pinned (mobile tap), hover does nothing
      if (this.popPinned) return;

      // Track latest pointer for delayed show (mouse moves fast)
      this._lastPointer = pointer || this._lastPointer;

      // Cancel pending timers
      if (this._hoverTimer){ clearTimeout(this._hoverTimer); this._hoverTimer = null; }
      if (this._hideTimer){ clearTimeout(this._hideTimer); this._hideTimer = null; }

      // Same card: if pop already visible, just update position/content; otherwise keep waiting
      if (index === this.hoverIndex){
        if (index >= 0 && this.popActive){
          this.showPop(index, this._lastPointer);
        }
        return;
      }

      this.hoverIndex = index;

      if (index < 0){
        // Small hide delay to avoid flicker when crossing gaps
        this._hideTimer = setTimeout(() => {
          if (!this.popPinned){
            this.hidePop();
          }
        }, this.hoverHideDelayMs);
        return;
      }

      // Delay show to avoid popup spam on quick pass
      const expectedIndex = index;
      this._hoverTimer = setTimeout(() => {
        this._hoverTimer = null;
        if (this.popPinned) return;
        if (this.hoverIndex !== expectedIndex) return;
        this.popActive = true;
        this.showPop(expectedIndex, this._lastPointer);
      }, this.hoverDelayMs);
    }

    togglePin(index, pointer){
      if (index < 0){
        this.popPinned = false;
        this.hidePop();
        return;
      }
      if (this.popPinned && this.hoverIndex === index){
        // close
        this.popPinned = false;
        this.hidePop();
        this.hoverIndex = -1;
        return;
      }
      this.popPinned = true;
      this.hoverIndex = index;
      this.showPop(index, pointer);
    }

    showPop(index, pointer){
      const item = this.items[index];
      if (!item) return;

      // Build content from available info (may be partial)
      const info = item.info || {};
      setHoverContent(info);

      // Position from pointer (screen coords) -> client coords
      let cx = 0, cy = 0;
      try{
        const rect = pointer?.rect || this.app?.canvas?.getBoundingClientRect?.();
        if (rect){
          const clientX = rect.left + (pointer.x / this.app.screen.width) * rect.width;
          const clientY = rect.top  + (pointer.y / this.app.screen.height) * rect.height;
          cx = clientX; cy = clientY;
        }
      }catch(e){}
      positionHoverEl(cx, cy);

      // Debug log (rate-limited)
      if (DEBUG){
        const now = performance.now();
        if (now - this._lastLogTs > 800){
          this._lastLogTs = now;
          try{ console.log(`[Pixi] hover -> ${info.name || info.idHint || '—'} | tapped=${!!info.tapped}`); }catch(e){}
        }
      }
    }

    hidePop(){
      this.popActive = false;
      hideHoverEl();
    }

    tick(){
      // No Pixi pop animation anymore (DOM hover is above all layers).
    }
  }

  class PixiBoard {
    constructor(){
      this.zones = new Map();
      this.ready = false;
      this._binded = false;
      this._lastClient = { x: 0, y: 0 };
      this._lastPointerTs = 0;
      this._scrollFling = null;
    }

    async init(){
      if (!window.PIXI) return;

      for (const id of ['myTerrainZone','oppTerrainZone']){
        const el = document.getElementById(id);
        if (!el) continue;
        const zr = new ZoneRenderer(el);
        await zr.init();
        this.zones.set(id, zr);
      }

      ensureCardsLoaded();
      this.bindGlobalPointer();
      this.ready = true;
    }

    bindGlobalPointer(){
      if (this._binded) return;
      this._binded = true;


      const stopFling = () => {
        if (this._scrollFling && this._scrollFling.raf){
          cancelAnimationFrame(this._scrollFling.raf);
        }
        this._scrollFling = null;
      };

      const startFling = (zr, v0) => {
        // v0 is in px/ms (positive = scroll down)
        stopFling();
        const fling = { zr, v: v0, lastTs: performance.now(), raf: 0 };
        const frictionPer16 = 0.92; // higher = longer glide
        const minV = 0.02; // px/ms
        const step = (now) => {
          if (!this.ready || !fling.zr || !fling.zr.scrollEnabled || fling.zr.scrollMax <= 0){
            stopFling();
            return;
          }
          const dt = Math.min(34, Math.max(0, now - fling.lastTs));
          fling.lastTs = now;

          // Integrate
          const next = fling.zr.scrollY + fling.v * dt;
          fling.zr.setScroll(next);

          // Decay velocity
          const decay = Math.pow(frictionPer16, dt / 16);
          fling.v *= decay;

          // Stop at bounds (with a tiny damp)
          if (fling.zr.scrollY <= 0 || fling.zr.scrollY >= fling.zr.scrollMax){
            fling.v *= 0.4;
          }

          if (Math.abs(fling.v) < minV){
            stopFling();
            return;
          }
          fling.raf = requestAnimationFrame(step);
          this._scrollFling = fling;
        };
        fling.raf = requestAnimationFrame(step);
        this._scrollFling = fling;
      };

      const onMove = (ev) => {
        if (!this.ready) return;
        const x = ev.clientX;
        const y = ev.clientY;
        this._lastClient = { x, y };
        this._lastPointerTs = performance.now();


        // If a scroll-drag is in progress (crowded terrain), update scroll with velocity (smooth) and skip hover.
        if (this._scrollDrag && this._scrollDrag.zr){
          const d = this._scrollDrag;
          const dyTotal = y - d.startY;
          const dySince = y - (d.lastY ?? y);
          const now = performance.now();
          const dt = Math.max(8, now - (d.lastTs ?? now));
          const instV = (-dySince) / dt; // px/ms in scroll-space

          // Activate drag sooner (feels snappier on mobile)
          if (d.active || Math.abs(dyTotal) > 4){
            d.active = true;
            // Low-pass filter velocity for stable inertia
            d.v = (d.v == null) ? instV : (0.8*d.v + 0.2*instV);
            d.lastY = y;
            d.lastTs = now;

            d.zr.setScroll(d.startScroll - dyTotal);
            this._touchActive = false;
            return;
          } else {
            d.lastY = y;
            d.lastTs = now;
          }
        }


        for (const zr of this.zones.values()){
          const sp = zr.screenFromClient(x, y);
          if (!sp){
            zr.setHover(-1, null);
            continue;
          }
          const idx = zr.hitTest(sp.x, sp.y);
          zr.setHover(idx, sp);
        }
      };

      const onDown = (ev) => {
        if (!this.ready) return;
        const x = ev.clientX;
        const y = ev.clientY;

        const isTouch = (ev.pointerType === 'touch') || ('ontouchstart' in window);

        if (!isTouch) return; // desktop hover already handled by pointermove

        stopFling();


        // Prepare a possible scroll-drag if we touch inside a crowded terrain zone.
        this._scrollDrag = null;
        for (const zr of this.zones.values()){
          const sp0 = zr.screenFromClient(x, y);
          if (!sp0) continue;
          if (zr.scrollEnabled && (zr.scrollMax > 0)){
            this._scrollDrag = { zr, startY: y, startScroll: zr.scrollY, active: false, lastY: y, lastTs: performance.now(), v: 0 };
          }
          break;
        }

        // Touch behavior: show while finger is down, hide on release.
        let anyShown = false;
        for (const zr of this.zones.values()){
          const sp = zr.screenFromClient(x, y);
          if (!sp) { zr.setHover(-1, null); continue; }
          const idx = zr.hitTest(sp.x, sp.y);
          zr.popPinned = false; // ensure no "pinned" mode
          zr.setHover(idx, sp);
          if (idx >= 0) anyShown = true;
        }
        this._touchActive = anyShown;
      };

      const onUp = (ev) => {
        if (!this.ready) return;

        // 🔧 Toujours cacher le hover tooltip dès que le doigt lève (même pendant scroll-drag)
        hideHoverEl();

        // If we were dragging to scroll a crowded zone, end drag and optionally start inertia.
        const d = this._scrollDrag;
        this._scrollDrag = null;
        if (d && d.active && d.zr && d.zr.scrollEnabled && d.zr.scrollMax > 0){
          const v = (typeof d.v === 'number') ? d.v : 0;
          if (Math.abs(v) > 0.06){ // px/ms (~60px/s)
            startFling(d.zr, v);
          }
          // Hide all pixi hovers
          for (const zr of this.zones.values()){ zr.popPinned = false; zr.setHover(-1, null); }
          return;
        }

        if (!this._touchActive) return;
        this._touchActive = false;
        // Hide all popouts immediately
        for (const zr of this.zones.values()){
          zr.popPinned = false;
          zr.setHover(-1, null);
        }
      };

            // Use pointer events when available
      window.addEventListener('pointermove', onMove, { passive: true });
      window.addEventListener('pointerdown', onDown, { passive: true });
      window.addEventListener('pointerup', onUp, { passive: true });
      window.addEventListener('pointercancel', onUp, { passive: true });


      // Desktop wheel scroll for crowded terrain zones
      window.addEventListener('wheel', (ev) => {
        if (!this.ready) return;
        const x = ev.clientX;
        const y = ev.clientY;
        for (const zr of this.zones.values()){
          const sp = zr.screenFromClient(x, y);
          if (!sp) continue;
          if (zr.scrollEnabled && zr.scrollMax > 0){
            zr.setScroll(zr.scrollY + ev.deltaY);
          }
          break;
        }
      }, { passive: true });
      const onLeave = () => {
        if (!this.ready) return;
        // Mouse left window or tab blurred: hide any hover card
        for (const zr of this.zones.values()){
          zr.popPinned = false;
          zr.setHover(-1, null);
        }
      };
      window.addEventListener('blur', onLeave, { passive: true });
      document.addEventListener('mouseleave', onLeave, { passive: true });

      if (DEBUG){
        // eslint-disable-next-line no-console
        console.log('[Pixi] pointer listeners attached (read-only overlay, DOM clicks still pass through).');
      }
    }

    renderZone(zoneId, terrain){
      if (!this.ready) return;
      const key = normZoneId(zoneId);
      const zr = this.zones.get(key);
      if (!zr) return;

      if (DEBUG){
        // Rate-limited bridge log: proves data arrives
        const now = performance.now();
        if (!this._lastBridgeLogTs || (now - this._lastBridgeLogTs > 1000)){
          this._lastBridgeLogTs = now;
          const arr = normalizeTerrain(terrain);
          const n = arr.length;
          try{ if (DEBUG && arr && arr[0]){ const k = Object.keys(arr[0]).slice(0, 30); console.log('[Pixi] sample keys', k); window.__pixiLastItem = arr[0]; } }catch(e){}
          const shape = debugTerrainShape(terrain);
          // eslint-disable-next-line no-console
          console.log(`[Pixi] bridge __pixiRenderTerrain -> ${key} (items=${n})`, shape);
        }
      }

      zr.render(terrain);
    }
  }

  const board = new PixiBoard();

  function start(){
    board.init().then(() => {
      window.__pixiRenderTerrain = (zoneId, terrain) => {
        try { board.renderZone(zoneId, terrain); } catch(e){}
      };
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();